/**
 * MessageBubble — top-level dispatcher that picks the right per-type bubble
 * subcomponent (TextBubble, GiftBubble, ImageBubble, VideoBubble,
 * VideoCallBubble, ShareUserBubble, ShareContentBubble) based on the
 * message `type` field. Subcomponents self-register against the registry
 * in `../../registry/bubbleRegistry` so the dispatcher does not import them
 * directly (avoids circular imports). The barrel (`./index.ts`) imports
 * each subcomponent for its side-effect.
 *
 * Flutter source of truth:
 *  - `currentUserMessageContentByType` — `chat_screen.dart:1365-1394`.
 *  - `peerUserMessageContentByType`    — `chat_screen.dart:2478-2506`.
 *
 * Both Flutter switches dispatch on `ChatMessageType.<x>.index`. They are
 * structurally identical — the only difference is which per-type
 * subcomponent they call (current-user variant vs peer-user variant). On
 * web both variants live INSIDE the same per-type subcomponent, gated by
 * `isOwn`. `ChatMessageType.reply` (index 4) intentionally falls through
 * to `null` here, mirroring Flutter's `Container()` at
 * `chat_screen.dart:1393` / `:2505`.
 *
 * Helpers live in sibling files:
 *  - `@/domain/utils/chatUtils` — `ChatUtils.getBubbleCornerRadii`,
 *    `ChatUtils.isLastMessageRight`, `ChatUtils.isLastMessageLeft`,
 *    `ChatUtils.getReadReceiptMode`, `BubbleCornerRadii`, `ReadReceiptMode`.
 *  - `../../registry/bubbleRegistry` — `BubbleSubcomponentProps`,
 *    `registerBubble`, `getRegisteredBubble`.
 *
 * Neighbor-aware data note: the dispatcher does NOT receive `index` or the
 * full `messages` array — `ChatMessagesList.renderItem` precomputes
 * `isLastOfRun` (via `ChatUtils.isLastMessageRight` / `isLastMessageLeft`)
 * and `readReceiptMode` (via `ChatUtils.getReadReceiptMode`) once per message
 * and threads them down as primitives. This keeps each leaf's render input
 * scoped to its own data so React can short-circuit per-message renders, and
 * matches the user-review fix from Task 25.
 *
 * NOTE on `_messageTimeSeen` (Flutter `Map<int, bool>` keyed by
 * `timestamp`, `chat_screen.dart:1405-1414`): the dispatcher does NOT own
 * that map. The chat hook (`useChat`) tracks which timestamp is open via
 * `isTimestampOpen(timestamp)` and the toggle is fired via
 * `onToggleTimestamp`. Each subcomponent's tap handler calls
 * `onToggleTimestamp(message.timestamp)` (NOT a generic toggle) so the
 * hook preserves the "only one open at a time" invariant centrally.
 */

import React from 'react';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ReadReceiptMode } from '@/domain/enums/chat/readReceiptMode';
import { ReadReceiptAvatar } from './ReadReceiptAvatar';
import { PeerProfileAvatar } from './PeerProfileAvatar';
import { MessageTimestamp } from './MessageTimestamp';
import { getRegisteredBubble } from '../../registry/bubbleRegistry';

/**
 * Public props for the dispatcher itself.
 */
export interface MessageBubbleProps {
  message: ChatMessageEntity;
  peerUser: UserEntity | null;
  currentUserId: string;
  /**
   * True iff this bubble is the run-end of its sender. Precomputed by
   * `ChatMessagesList.renderItem` using `ChatUtils.isLastMessageRight` (own)
   * / `ChatUtils.isLastMessageLeft` (peer) so the dispatcher does not have to
   * walk the message list per render. Mirrors Flutter
   * `chat_screen.dart:740-749` (own) / `727-738` (peer).
   */
  isLastOfRun: boolean;
  /**
   * Precomputed read-receipt mode for this message. Produced by
   * `ChatUtils.getReadReceiptMode` once per render in
   * `ChatMessagesList.renderItem` — replaces the previous `index +
   * myLastReadPosition` pair so the dispatcher does not have to keep the
   * walked-list result around per bubble. Mirrors Flutter
   * `currentUserMessageStatus` (`chat_screen.dart:2252-2293`).
   * Only meaningful for own bubbles; peer bubbles never render a receipt.
   */
  readReceiptMode: ReadReceiptMode;
  /** Whether THIS message's timestamp is currently expanded. */
  isTimestampOpen: boolean;
  /** Toggle handler (see `BubbleSubcomponentProps.onToggleTimestamp`). */
  onToggleTimestamp: (timestamp: number) => void;
  /** Retry handler (see `BubbleSubcomponentProps.onRetry`). */
  onRetry: (message: ChatMessageEntity) => void;
  /** Audience-join handler (see `BubbleSubcomponentProps.onVideoCallJoin`). */
  onVideoCallJoin?: (message: ChatMessageEntity) => void;
  /** Screen key of the hosting page. */
  screenKey: string;
}

/**
 * The dispatcher. Computes `isOwn`, looks up the per-type subcomponent in
 * the registry, and wraps it with the three adornments
 * (`PeerProfileAvatar` / `ReadReceiptAvatar` / `MessageTimestamp`). Returns
 * `null` for unregistered types (including `ChatMessageType.reply`).
 *
 * Layout mirrors Flutter's two-level structure:
 *  - Own bubbles:  `Column[ Row[ bubble, ReadReceiptAvatar ], MessageTimestamp ]`
 *    (`chat_screen.dart:1316-1330`).
 *  - Peer bubbles: `Column[ Row[ PeerProfileAvatar, bubble ], MessageTimestamp ]`
 *    (`chat_screen.dart:2353-2375`).
 *
 * Per-type subcomponents handle only the bubble itself — alignment,
 * background, content, tap-to-retry. The adornments wrap every bubble
 * from here so each subcomponent stays focused on its content.
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  peerUser,
  currentUserId,
  isLastOfRun,
  readReceiptMode,
  isTimestampOpen,
  onToggleTimestamp,
  onRetry,
  onVideoCallJoin,
  screenKey,
}) => {
  const responsive = useResponsiveDesign();
  const isOwn = message.idFrom === currentUserId;

  const Component = getRegisteredBubble(message.type);
  if (!Component) {
    // Reply (4) and any unregistered type → `null`. Matches Flutter's
    // `Container()` fall-through at `chat_screen.dart:1393` / `:2505`.
    return null;
  }

  const bubble = (
    <Component
      message={message}
      peerUser={peerUser}
      currentUserId={currentUserId}
      isOwn={isOwn}
      isLastOfRun={isLastOfRun}
      readReceiptMode={readReceiptMode}
      isTimestampOpen={isTimestampOpen}
      onToggleTimestamp={onToggleTimestamp}
      onRetry={onRetry}
      onVideoCallJoin={onVideoCallJoin}
      screenKey={screenKey}
    />
  );

  // Inner row: side-adornment + bubble. The cross-axis alignment is `end`
  // (`CrossAxisAlignment.end` in both Flutter rows at `chat_screen.dart:1350`
  // / `:2435`) so the avatar / receipt hugs the bottom of the bubble.
  //
  // Row max-width mirrors Flutter's `ConstrainedBox` at
  // `chat_screen.dart:1341-1347` (own) and `:2428-2434` (peer): text bubbles
  // are capped at `MediaQuery.size.width * 3/4`, every other type uses the
  // full viewport width. Web mirrors this via `responsive.chat.bubbleMaxWidth`
  // (matching the same `vw` convention `UnreadBanner` uses for Flutter's
  // `width / 2`). Non-text bubbles get `'100%'` — equivalent to Flutter's
  // full-width branch since the row is already bounded by its parent (the
  // chat surface fills the viewport on the chat page).
  const isTextMessage = message.type === ChatMessageType.text;
  const row = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        gap: responsive.spacing.xs,
        // Keep multi-line bubbles from forcing the row wider than the parent.
        maxWidth: isTextMessage ? responsive.chat.bubbleMaxWidth : '100%',
      }}
    >
      {isOwn ? (
        <>
          {bubble}
          <ReadReceiptAvatar
            mode={readReceiptMode}
            peerUser={peerUser}
            qa={{
              id: `chat-read-receipt-${message.documentId}`,
              // The avatar (lastReadAvatar) means the peer has read this
              // message — surface `read` so the agent can assert it directly.
              status:
                readReceiptMode === ReadReceiptMode.lastReadAvatar
                  ? 'read'
                  : undefined,
            }}
          />
        </>
      ) : (
        <>
          <PeerProfileAvatar
            isLastOfRun={isLastOfRun}
            peerUser={peerUser}
            qa={{ id: `chat-peer-avatar-${peerUser?.firebaseId ?? 'unknown'}` }}
          />
          {bubble}
        </>
      )}
    </div>
  );

  // Delivery state for the qa-tester (`data-qa-status`). Derived purely from
  // fields the message already carries — no new component state. Only own
  // bubbles have a meaningful delivery lifecycle; peer bubbles emit no status.
  //   failed  → `success === false` (send failed; mirrors the retry branch)
  //   sending → `isUploading === true` (media optimistic tile mid-upload)
  //   read    → `seenByPeer === true` (peer has read it)
  //   sent    → otherwise
  const deliveryStatus: string | undefined = !isOwn
    ? undefined
    : message.success === false
      ? 'failed'
      : message.isUploading === true
        ? 'sending'
        : message.seenByPeer === true
          ? 'read'
          : 'sent';

  return (
    <div
      {...qaAttr({
        // Canonical list-row example from `qaAttr.ts` — `chat-message-<id>`.
        // `documentId` is the per-message UUID (optimistic insert or Firestore
        // doc identity), guaranteed unique across both paths.
        id: `chat-message-${message.documentId}`,
        value: isOwn ? 'me' : 'peer',
        status: deliveryStatus,
      })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        // Own column is right-aligned, peer column is left-aligned (Flutter
        // `CrossAxisAlignment.end` on own at `chat_screen.dart:1319` and
        // `CrossAxisAlignment.start` on peer at `chat_screen.dart:2358`).
        alignItems: isOwn ? 'flex-end' : 'flex-start',
      }}
    >
      {row}
      <MessageTimestamp
        timestamp={message.timestamp}
        isOwn={isOwn}
        isLastOfRun={isLastOfRun}
        isTimestampOpen={isTimestampOpen}
      />
    </div>
  );
};
